import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useMLO } from "@/lib/mloContext";
import { CONTACT } from "@/lib/constants";

export default function FAQ() {
  const mlo = useMLO();
  const full = mlo?.name || CONTACT.loanOfficer;
  const first = full.split(" ")[0];
  const nmls = mlo?.nmls || CONTACT.nmls;
  const phone = mlo?.phone || CONTACT.phoneDisplay;
  const phoneHref = mlo?.phone ? `tel:${mlo.phone.replace(/[^+0-9]/g, "")}` : CONTACT.phoneHref;
  const yearsClause = mlo?.years_experience
    ? `with more than ${mlo.years_experience} years of experience`
    : "";

  const faqs = [
    {
      q: "Will checking my rate hurt my credit score?",
      a: "No. Starting your rate check with Great Rate Check is a soft inquiry and won't impact your credit. A hard pull only happens once you decide to move forward with a formal loan application.",
    },
    {
      q: "How is Great Rate Check different from a rate-comparison website?",
      a: `Most comparison sites sell your info to multiple lenders. We don't. You work directly with ${full} at NEXA Lending, who shops NEXA's wholesale lender network to find you a competitive personalized rate.`,
    },
    {
      q: `Who is ${full} and what is NEXA Lending?`,
      a: `${full} (NMLS #${nmls}) is a licensed mortgage loan officer${yearsClause ? ` ${yearsClause}` : ""}, serving clients nationally. NEXA Lending (NMLS #${CONTACT.companyNmls}) is one of the largest mortgage brokerages in the U.S., giving ${first} access to a broad network of wholesale lenders and loan programs.`,
    },
    {
      q: "What loan types can I qualify for?",
      a: "Through NEXA Lending's lender network, you have access to Conventional, FHA, VA, USDA, Jumbo, DSCR, Non-QM, and specialty programs for self-employed borrowers, investors, and more.",
    },
    {
      q: "Is my personal information safe?",
      a: "Yes. Your info is transmitted over encrypted connections and stored securely. We never sell or share your data with third-party marketers — it's used solely to prepare your rate scenarios and follow up with you.",
    },
    {
      q: "How quickly will I hear back?",
      a: `You'll typically hear from ${first} within one business day. Need to move faster? You can also call or text directly at ${phone}.`,
    },
    {
      q: "In which states are you licensed?",
      a: `${first} and NEXA Lending are licensed in a majority of U.S. states. See the state list in the footer, or just ask — if we can't serve you directly, we'll point you to a trusted licensed professional.`,
    },
  ];

  return (
    <section id="faq" data-testid="faq-section" className="section-y bg-white">
      <div className="container-grc max-w-4xl">
        <div className="text-center">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">FAQ</p>
          <h2 className="mt-4 font-heading font-medium text-4xl sm:text-5xl text-balance">
            Answers to the questions we hear most.
          </h2>
          <p className="mt-4 text-ink-600 text-lg">
            Don't see yours? Text {first} directly at{" "}
            <a className="text-teal underline underline-offset-4 hover:text-teal-hover" href={phoneHref}>
              {phone}
            </a>.
          </p>
        </div>

        <Accordion type="single" collapsible className="mt-10">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-b border-linen-border">
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left font-heading font-medium text-lg sm:text-xl text-navy hover:no-underline hover:text-teal transition-colors py-5"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-content-${i}`}
                className="text-base leading-relaxed text-ink-600 pb-6"
              >
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
